import React, { useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip,
} from 'recharts';
import { Loader2, GitCompareArrows, Lightbulb, UserX } from 'lucide-react';
import { atlasApi, AtlasUser, DiscComparar, DiscFator, DiscPessoaCmp, PerfilDisc } from '../shared/atlasApi';
import { inputCls, labelCls } from '../shared/ListaEditavel';

interface Props {
  user: AtlasUser;
}

const DISC_CORES: Record<DiscFator, string> = { D: '#e11d48', I: '#f59e0b', S: '#10b981', C: '#3b82f6' };
const DISC_LABEL: Record<DiscFator, string> = { D: 'Dominância', I: 'Influência', S: 'Estabilidade', C: 'Conformidade' };
const FATORES: DiscFator[] = ['D', 'I', 'S', 'C'];

const COR_A = '#7c3aed'; // purple
const COR_B = '#0ea5e9'; // sky

function nomePessoa(p?: DiscPessoaCmp | null, fallback = '—'): string {
  if (!p) return fallback;
  return p.nome || `#${p.funccodi}`;
}

const PessoaSemDisc: React.FC<{ p: DiscPessoaCmp; cor: string }> = ({ p, cor }) => (
  <div className="flex items-center gap-2 text-sm">
    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: cor }} />
    <span className="font-medium text-gray-800">{nomePessoa(p)}</span>
    <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
      <UserX className="w-3.5 h-3.5" /> sem DISC
    </span>
  </div>
);

const CompararDupla: React.FC<Props> = ({ user }) => {
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [dados, setDados] = useState<DiscComparar | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const comparar = () => {
    const na = Number(a);
    const nb = Number(b);
    if (!na || !nb) { setErro('Informe os dois códigos de funcionário (funccodi).'); return; }
    setLoading(true);
    setErro(null);
    setDados(null);
    atlasApi.getDiscComparar(user, na, nb)
      .then(r => setDados(r))
      .catch(e => setErro(e.message || 'Erro ao comparar'))
      .finally(() => setLoading(false));
  };

  const temPerfilA = !!dados?.a?.perfil;
  const temPerfilB = !!dados?.b?.perfil;

  const radarData = dados ? FATORES.map(f => ({
    fator: f,
    [nomePessoa(dados.a, 'A')]: dados.a?.perfil?.[f as keyof PerfilDisc] ?? 0,
    [nomePessoa(dados.b, 'B')]: dados.b?.perfil?.[f as keyof PerfilDisc] ?? 0,
  })) : [];

  return (
    <div className="space-y-4 max-w-4xl">
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
        <GitCompareArrows className="w-5 h-5 text-purple-600" /> Comparar dupla
      </h2>

      {/* Seletores */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className={labelCls}>Funcionário A (funccodi)</label>
            <input
              className={inputCls}
              type="number"
              min={1}
              value={a}
              onChange={e => setA(e.target.value)}
              placeholder="Ex.: 1234"
              onKeyDown={e => { if (e.key === 'Enter') comparar(); }}
            />
          </div>
          <div>
            <label className={labelCls}>Funcionário B (funccodi)</label>
            <input
              className={inputCls}
              type="number"
              min={1}
              value={b}
              onChange={e => setB(e.target.value)}
              placeholder="Ex.: 5678"
              onKeyDown={e => { if (e.key === 'Enter') comparar(); }}
            />
          </div>
          <button
            onClick={comparar}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg h-[42px]"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompareArrows className="w-4 h-4" />} Comparar
          </button>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          Dica: use o Mapa do time para descobrir os códigos (funccodi) dos colaboradores.
        </p>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {dados && (
        <>
          {/* Cabeçalho das duas pessoas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[{ p: dados.a, cor: COR_A }, { p: dados.b, cor: COR_B }].map(({ p, cor }, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: cor }} />
                  <span className="font-bold text-gray-900">{nomePessoa(p)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{p?.cargo || 'Sem cargo'}</div>
                {p?.estilo_principal ? (
                  <span className="inline-flex mt-2 items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: DISC_CORES[(p.estilo_principal.charAt(0).toUpperCase() as DiscFator)] || '#9ca3af' }}>
                    {p.estilo_principal}
                  </span>
                ) : (
                  <span className="inline-flex mt-2 items-center gap-1 text-amber-600 text-xs"><UserX className="w-3.5 h-3.5" /> sem DISC</span>
                )}
              </div>
            ))}
          </div>

          {(!temPerfilA || !temPerfilB) && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-1">
              <p className="text-sm font-medium text-amber-800">Comparação parcial</p>
              {!temPerfilA && <PessoaSemDisc p={dados.a} cor={COR_A} />}
              {!temPerfilB && <PessoaSemDisc p={dados.b} cor={COR_B} />}
              <p className="text-xs text-amber-700">Quem não respondeu o questionário aparece com valores zerados.</p>
            </div>
          )}

          {/* Radar sobreposto */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Perfis sobrepostos</h3>
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={radarData} outerRadius="70%">
                <PolarGrid />
                <PolarAngleAxis dataKey="fator" tick={{ fontSize: 13, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Radar name={nomePessoa(dados.a, 'A')} dataKey={nomePessoa(dados.a, 'A')} stroke={COR_A} fill={COR_A} fillOpacity={0.2} />
                <Radar name={nomePessoa(dados.b, 'B')} dataKey={nomePessoa(dados.b, 'B')} stroke={COR_B} fill={COR_B} fillOpacity={0.2} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Tabela com gaps */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Diferença por fator</h3>
            <table className="w-full text-sm">
              <thead className="text-[11px] text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left py-1">Fator</th>
                  <th className="text-center py-1">{nomePessoa(dados.a, 'A')}</th>
                  <th className="text-center py-1">{nomePessoa(dados.b, 'B')}</th>
                  <th className="text-center py-1">Gap</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {FATORES.map(f => {
                  const va = dados.a?.perfil?.[f as keyof PerfilDisc] ?? 0;
                  const vb = dados.b?.perfil?.[f as keyof PerfilDisc] ?? 0;
                  const gap = dados.gaps?.[f as keyof PerfilDisc] ?? Math.abs(va - vb);
                  return (
                    <tr key={f}>
                      <td className="py-2">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: DISC_CORES[f] }} />
                          <span className="text-gray-800">{DISC_LABEL[f]}</span>
                        </span>
                      </td>
                      <td className="text-center text-gray-700">{va}%</td>
                      <td className="text-center text-gray-700">{vb}%</td>
                      <td className={`text-center font-semibold ${gap >= 30 ? 'text-rose-600' : gap >= 15 ? 'text-amber-600' : 'text-emerald-600'}`}>{gap}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Dicas */}
          {dados.dicas && dados.dicas.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" /> Dicas de convivência
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {dados.dicas.map((d, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-800">
                    <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
                    <span>{d}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CompararDupla;
